import { defineStore } from 'pinia';
import {
  addPopularCompetition,
  addPopularFixture,
  deletePopularCompetition,
  deletePopularFixture,
  fetchFavoritesRegions,
  fetchFavoritesSourceCompetitions,
  fetchFavoritesSourceFixtures,
  fetchFavoritesSports,
  fetchPopularCompetitions,
  fetchPopularFixtures,
} from '@/api/admin-be';
import { catchError } from '@/helpers/toast';

type IdName = {
  Id: number;
  Name: string;
};

type PopularCompetition = {
  competition_id: number;
  competition_name: string;
  fixtures_count: number;
  first_time_utc: string;
  last_time_utc: string;
  sport_ids: number[];
};

type PopularFixture = {
  id: number;
  name: string;
  start_time_utc: string;
  event_status_type: string;
  competition_id: number;
  competition_name: string;
  region_id: number;
  region_name: string;
  sport_id: number;
  sport_name: string;
};

export type Fixture = PopularFixture;

type PopularCompetitionsResponse = {
  competitions: PopularCompetition[];
  count: number;
};

type CompetitionWithCounts = {
  Id: number;
  Name: string;
  _count: { [key: string]: number };
};

type CompetitionsResponse = {
  competitions: CompetitionWithCounts[];
  count: number;
};

type PopularFixturesResponse = {
  fixtures: PopularFixture[];
  count: number;
};

type FixturesResponse = {
  fixtures: Fixture[];
  count: number;
};

export const useFavoritesStore = defineStore('favorites', {
  state: () => ({
    sportsLoading: false,
    sports: null as null | IdName[],
    regions: null as null | IdName[],

    competitionsLoading: false,
    competitions: null as null | CompetitionWithCounts[],
    competitionsCount: 0,
    competitionsOffset: 0,
    competitionsPageSize: 15,

    popularCompetitionsLoading: false,
    popularCompetitions: null as null | PopularCompetition[],
    popularCompetitionsCount: 0,
    popularCompetitionsOffset: 0,
    popularCompetitionsPageSize: 15,

    fixturesLoading: false,
    fixtures: null as null | Fixture[],
    fixturesCount: 0,
    fixturesOffset: 0,
    fixturesPageSize: 15,

    popularFixturesLoading: false,
    popularFixtures: null as null | PopularFixture[],
    popularFixturesCount: 0,
    popularFixturesOffset: 0,
    popularFixturesPageSize: 15,
  }),
  actions: {
    async fetchSports() {
      this.sportsLoading = true;
      return fetchFavoritesSports()
        .then((result) => {
          this.sports = result;
        })
        .catch((error) => catchError('Ошибка загрузки видов спорта', error))
        .finally(() => (this.sportsLoading = false));
    },
    async fetchRegions() {
      this.sportsLoading = true;
      return fetchFavoritesRegions()
        .then((result) => {
          this.regions = result;
        })
        .catch((error) => catchError('Ошибка загрузки регионов', error))
        .finally(() => (this.sportsLoading = false));
    },

    async fetchPopularCompetitions() {
      this.popularCompetitionsLoading = true;
      return fetchPopularCompetitions()
        .then((result: PopularCompetitionsResponse) => {
          this.popularCompetitions = result.competitions;
          this.popularCompetitionsCount = result.count;
        })
        .catch((error) => catchError('Ошибка загрузки популярных турниров', error))
        .finally(() => (this.popularCompetitionsLoading = false));
    },
    async deletePopularCompetition(competitionId: number) {
      return deletePopularCompetition(competitionId)
        .then(() => {
          this.popularCompetitions =
            this.popularCompetitions?.filter((c) => c.competition_id !== competitionId) ?? null;
        })
        .catch((error) => catchError('Ошибка удаления популярного турнира', error));
    },
    async addPopularCompetition(competitionId: number) {
      return addPopularCompetition(competitionId)
        .then(() => this.fetchPopularCompetitions())
        .catch((error) => catchError('Ошибка добавления популярного турнира', error));
    },

    async fetchPopularFixtures() {
      this.popularFixturesLoading = true;
      return fetchPopularFixtures()
        .then((result: PopularFixturesResponse) => {
          this.popularFixtures = result.fixtures;
          this.popularFixturesCount = result.count;
        })
        .catch((error) => catchError('Ошибка загрузки популярных матчей', error))
        .finally(() => (this.popularFixturesLoading = false));
    },
    async deletePopularFixture(fixtureId: number) {
      return deletePopularFixture(fixtureId)
        .then(() => {
          this.popularFixtures = this.popularFixtures?.filter((c) => c.id !== fixtureId) ?? null;
        })
        .catch((error) => catchError('Ошибка удаления популярного матча', error));
    },
    async addPopularFixture(fixtureId: number) {
      return addPopularFixture(fixtureId)
        .then(() => this.fetchPopularFixtures())
        .catch((error) => catchError('Ошибка добавления популярного матча', error));
    },

    async fetchCompetitions(filters?: any) {
      this.competitionsLoading = true;
      const paginatedFilters = {
        ...filters,
        offset: this.competitionsOffset,
        pageSize: this.competitionsPageSize,
      };

      return fetchFavoritesSourceCompetitions(paginatedFilters)
        .then((result: CompetitionsResponse) => {
          const page = Math.floor(this.competitionsOffset / this.competitionsPageSize);
          const maxPage = Math.floor(result.count / this.competitionsPageSize);

          if (page > maxPage) {
            this.competitionsOffset = 0;
            this.fetchCompetitions(filters);
            return;
          }

          this.competitions = result.competitions;
          this.competitionsCount = result.count;
        })
        .catch((error) => catchError('Ошибка получения списка турниров', error))
        .finally(() => (this.competitionsLoading = false));
    },
    async fetchFixtures(filters?: any) {
      this.fixturesLoading = true;
      const paginatedFilters = {
        ...filters,
        offset: this.fixturesOffset,
        pageSize: this.fixturesPageSize,
      };
      return fetchFavoritesSourceFixtures(paginatedFilters)
        .then((result: FixturesResponse) => {
          const page = Math.floor(this.fixturesOffset / this.fixturesPageSize);
          const maxPage = Math.floor(result.count / this.fixturesPageSize);

          if (page > maxPage) {
            this.fixturesOffset = 0;
            this.fetchFixtures(filters);
            return;
          }

          this.fixtures = result.fixtures;
          this.fixturesCount = result.count;
        })
        .catch((error) => catchError('Ошибка получения списка матчей', error))
        .finally(() => (this.fixturesLoading = false));
    },
  },
});
